import {
  DEPLOYMENT_TYPE,
  LOG_ENABLE,
  LOG_FLUSH_INTERVAL_MS,
  LOG_MAX_BUFFER_SIZE,
  RELEASE_VERSION,
} from "../../config";
import { getCurrentUserIdentity } from "./currentUser";
import { getLogEndpoint } from "./endpoint";
import { redact } from "./redact";
import { getSessionId } from "./sessionId";

export type LogLevel = "debug" | "info" | "warn" | "error";

export type LogEventType =
  | "JS_ERROR"
  | "UNHANDLED_REJECTION"
  | "REACT_ERROR"
  | "API_SUCCESS"
  | "API_FAILURE"
  | "PAGE_VIEW"
  | "USER_ACTION";

const FLUSH_INTERVAL_MS = LOG_FLUSH_INTERVAL_MS;
const MAX_BUFFER_SIZE = LOG_MAX_BUFFER_SIZE;
// Cap while offline specifically — higher than MAX_BUFFER_SIZE because we
// deliberately let the buffer grow past the normal flush threshold during
// an outage (see flush() below) rather than attempt sends that would
// silently fail, so this needs its own, more generous ceiling.
const MAX_BUFFER_SIZE_WHILE_OFFLINE = Math.max(500, MAX_BUFFER_SIZE * 25);

const isEnabled = LOG_ENABLE === "Yes";

let buffer: Record<string, any>[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

const buildEvent = (
  event_type: LogEventType,
  level: LogLevel,
  details: Record<string, any>
) => {
  const identity = getCurrentUserIdentity();
  return {
    event_type,
    level,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_email: identity.email,
    user_name: identity.name,
    is_superuser: identity.is_superuser,
    url: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || undefined,
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    app_version: RELEASE_VERSION,
    deployment_type: DEPLOYMENT_TYPE,
    details: redact(details),
  };
};

const flush = () => {
  if (!isEnabled || buffer.length === 0) return;
  // No sink configured for this environment (REACT_APP_LOG_API_URL unset)
  // — nothing to send to, leave the buffer alone rather than drop it.
  if (!getLogEndpoint()) return;

  // sendBeacon() reports success once the browser has QUEUED the request —
  // not once it's actually delivered. During a real network outage it
  // still returns true, so clearing the buffer and calling it "sent" would
  // silently lose these events with no error to even notice. Skip sending
  // entirely while offline instead, and let the 'online' listener below
  // retry once connectivity is back — capped so a long outage can't grow
  // memory without bound.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    if (buffer.length > MAX_BUFFER_SIZE_WHILE_OFFLINE) {
      buffer = buffer.slice(buffer.length - MAX_BUFFER_SIZE_WHILE_OFFLINE);
    }
    return;
  }

  const batch = buffer;
  buffer = [];

  try {
    const payload = JSON.stringify(batch);
    const endpoint = getLogEndpoint();

    let sent = false;
    if (typeof navigator.sendBeacon === "function") {
      // The log-sink runs on a different port than the app (different
      // origin), so this is always a cross-origin beacon. `text/plain` is a
      // CORS-safelisted content type — it guarantees the browser treats this
      // as a "simple" request and actually delivers it, no preflight
      // involved. `application/json` is NOT safelisted, and cross-origin
      // sendBeacon calls with a non-simple content type can be silently
      // dropped after being queued (sendBeacon still returns true). The
      // sink doesn't care what Content-Type is declared — it just
      // JSON.parses the raw body regardless — so this costs nothing.
      const blob = new Blob([payload], { type: "text/plain;charset=UTF-8" });
      sent = navigator.sendBeacon(endpoint, blob);
    }

    if (!sent) {
      fetch(endpoint, {
        method: "POST",
        body: payload,
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        keepalive: true,
      }).catch(() => {
        // Genuine network/fetch failure (sendBeacon unavailable and the
        // fallback also failed) — requeue instead of dropping, same
        // failure-handling shape as the sink's own flush().
        buffer = batch.concat(buffer);
      });
    }
  } catch {
    // Synchronous failure (e.g. JSON.stringify threw) — requeue too.
    buffer = batch.concat(buffer);
  }
};

const push = (event: Record<string, any>) => {
  if (!isEnabled) return;
  buffer.push(event);
  if (buffer.length >= MAX_BUFFER_SIZE) {
    flush();
  }
};

const ensureFlushLoopStarted = () => {
  if (flushTimer || !isEnabled) return;

  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
  // Retry whatever accumulated during an outage the moment connectivity
  // is back, instead of waiting for the next 5s tick.
  window.addEventListener("online", flush);
};

ensureFlushLoopStarted();

export const logger = {
  debug: (message: string, details: Record<string, any> = {}) =>
    push(buildEvent("USER_ACTION", "debug", { message, ...details })),

  info: (message: string, details: Record<string, any> = {}) =>
    push(buildEvent("USER_ACTION", "info", { message, ...details })),

  warn: (message: string, details: Record<string, any> = {}) =>
    push(buildEvent("USER_ACTION", "warn", { message, ...details })),

  error: (message: string, details: Record<string, any> = {}) =>
    push(buildEvent("JS_ERROR", "error", { message, ...details })),

  reactError: (details: Record<string, any>) =>
    push(buildEvent("REACT_ERROR", "error", details)),

  unhandledRejection: (details: Record<string, any>) =>
    push(buildEvent("UNHANDLED_REJECTION", "error", details)),

  apiSuccess: (details: Record<string, any>) =>
    push(buildEvent("API_SUCCESS", "info", details)),

  apiFailure: (details: Record<string, any>) =>
    push(buildEvent("API_FAILURE", "error", details)),

  pageView: (details: Record<string, any>) =>
    push(buildEvent("PAGE_VIEW", "info", details)),

  action: (details: Record<string, any>) =>
    push(buildEvent("USER_ACTION", "info", details)),

  /** Forces an immediate flush — used on unload/visibility-change. */
  flush,
};
